/**
 * Deterministic automation surface.
 *
 * End-to-end tests cannot wave real hands at a real camera, so the app exposes a tiny,
 * clearly namespaced hook that injects landmark frames straight into the tracking pipeline.
 * Everything downstream — coordinate conversion, identity, smoothing, mapping, audio — runs
 * exactly as it does in production; only the landmark *source* is substituted.
 *
 * The hook is inert unless something calls it, and it exposes no capability a page script
 * could not already reach through the app's own UI.
 */

import type { AppState } from './AppStateMachine';
import type { SettingsStore, Settings } from './settings';
import type { LiveReadout } from '../ui/uiTypes';
import type { HandLandmarkSample } from '../tracking/types';
import { synthesizeHands } from '../tracking/syntheticHands';
import type { SyntheticHandOptions } from '../tracking/syntheticHands';

export interface TestHookHost {
  readonly stateName: AppState;
  readonly currentReadout: Readonly<LiveReadout>;
  readonly settingsStore: SettingsStore;
  readonly isMockTracking: boolean;
  injectInference(hands: HandLandmarkSample[], frameWidth: number, frameHeight: number): void;
  forceState(state: AppState): void;
}

export interface ThereminTestApi {
  readonly version: 1;
  getState(): AppState;
  getReadout(): LiveReadout;
  getSettings(): Settings;
  updateSettings(patch: Partial<Settings>): void;
  /** Inject fully specified landmark samples. */
  injectLandmarks(hands: HandLandmarkSample[], frameWidth?: number, frameHeight?: number): void;
  /** Inject hands described only by palm position, in image-normalized coordinates. */
  injectHands(specs: SyntheticHandOptions[], frameWidth?: number, frameHeight?: number): void;
  /** Inject an empty frame (all hands lost). */
  clearHands(frameWidth?: number, frameHeight?: number): void;
  isMockTracking(): boolean;
  forceState(state: AppState): void;
}

declare global {
  interface Window {
    __theremin?: ThereminTestApi;
  }
}

const DEFAULT_FRAME_WIDTH = 640;
const DEFAULT_FRAME_HEIGHT = 480;

export function installTestHooks(host: TestHookHost): void {
  if (typeof window === 'undefined') return;

  const api: ThereminTestApi = {
    version: 1,
    getState: () => host.stateName,
    getReadout: () => ({ ...host.currentReadout }),
    getSettings: () => ({ ...host.settingsStore.current }),
    updateSettings: (patch) => host.settingsStore.update(patch),
    injectLandmarks: (
      hands,
      frameWidth = DEFAULT_FRAME_WIDTH,
      frameHeight = DEFAULT_FRAME_HEIGHT,
    ) => host.injectInference(hands, frameWidth, frameHeight),
    injectHands: (specs, frameWidth = DEFAULT_FRAME_WIDTH, frameHeight = DEFAULT_FRAME_HEIGHT) =>
      host.injectInference(synthesizeHands(specs), frameWidth, frameHeight),
    clearHands: (frameWidth = DEFAULT_FRAME_WIDTH, frameHeight = DEFAULT_FRAME_HEIGHT) =>
      host.injectInference([], frameWidth, frameHeight),
    isMockTracking: () => host.isMockTracking,
    forceState: (state) => host.forceState(state),
  };

  window.__theremin = api;
}

export function removeTestHooks(): void {
  if (typeof window === 'undefined') return;
  delete window.__theremin;
}
